import { privateApi } from '@/config/axios.config';
import { CondominiumUnit, UnitStats, UnitCharacteristics } from '../types/unit.types';

const API_BASE = '/condominium/units';

export interface UnitCreateDTO {
  apartment: string;
  tower: string;
  type: 'apartment' | 'house' | 'commercial' | 'local' | 'parking' | 'storage';
  area: number;
  ownershipCoefficient?: number;
  condominiumId?: string;
  status?: 'occupied' | 'vacant' | 'maintenance' | 'reserved';
  ownerId?: string;
  tenantResidentId?: string;
  characteristics?: UnitCharacteristics;
  metadata?: Record<string, any>;
}

export interface UnitUpdateDTO extends Partial<UnitCreateDTO> {
  id?: string;
}

export interface UnitFilters {
  status?: 'occupied' | 'vacant' | 'maintenance' | 'reserved';
  type?: 'apartment' | 'house' | 'commercial' | 'local' | 'parking' | 'storage';
  tower?: string;
  apartment?: string;
  condominiumId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const unitsApiService = {
  // Obtener todas las unidades con filtros
  async getUnits(filters: UnitFilters = {}): Promise<{
    units: CondominiumUnit[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);
    if (filters.tower) params.append('tower', filters.tower);
    if (filters.apartment) params.append('apartment', filters.apartment);
    if (filters.condominiumId) params.append('condominiumId', filters.condominiumId);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await privateApi.get(`${API_BASE}?${params.toString()}`);
    return response.data?.data || response.data;
  },

  // Obtener una unidad por ID
  async getUnitById(id: string): Promise<CondominiumUnit> {
    const response = await privateApi.get(`${API_BASE}/${id}`);
    return response.data?.data || response.data;
  },

  // Crear nueva unidad
  async createUnit(data: UnitCreateDTO): Promise<CondominiumUnit> {
    const response = await privateApi.post(`${API_BASE}`, data);
    return response.data?.data || response.data;
  },

  // Actualizar unidad
  async updateUnit(id: string, data: UnitUpdateDTO): Promise<CondominiumUnit> {
    const response = await privateApi.patch(`${API_BASE}/${id}`, data);
    return response.data?.data || response.data;
  },

  // Eliminar unidad
  async deleteUnit(id: string): Promise<void> {
    await privateApi.delete(`${API_BASE}/${id}`);
  },

  // Obtener estadísticas de unidades
  async getUnitStats(): Promise<UnitStats> {
    const response = await privateApi.get(`${API_BASE}/stats`);
    return response.data?.data || response.data;
  },

  // Generar unidades masivamente
  async bulkGenerate(data: {
    towerType: 'tower' | 'manzana' | 'etapa' | 'interior';
    towerCount: number;
    apartmentsPerTower: number;
    numberingPattern: 'sequential' | 'floor-apartment' | 'custom';
    customPattern?: string;
    apartmentsPerFloor?: number;
    floorsPerTower?: number;
    unitType: 'apartment' | 'house' | 'commercial' | 'local' | 'parking' | 'storage';
    initialStatus: 'occupied' | 'vacant' | 'maintenance' | 'reserved';
    baseArea?: number;
    baseCharacteristics?: UnitCharacteristics;
  }): Promise<{
    totalGenerated: number;
    created: number;
    duplicates: number;
    errors: Array<{ tower: string; apartment: string; error: string }>;
  }> {
    const response = await privateApi.post(`${API_BASE}/bulk/generate`, data);
    return response.data?.data || response.data;
  },
};

export default unitsApiService;
